/**
 * Static US states + Canadian provinces reference data — the single source of truth for
 * state/province dropdowns and validation. Deliberately static constants (not a DB/GlobalCodes
 * lookup): this is immutable reference data, so a DB table would mean per-tenant duplication and
 * a needless migration. See Canada support plan §3.3 (revised) / §8.1.
 */
export interface AddressRegion {
  /** 2-letter code (e.g. "CA" = California, "ON" = Ontario). */
  code: string;
  name: string;
}

/** 50 states + DC + US territories. */
export const US_STATES: AddressRegion[] = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'PR', name: 'Puerto Rico' }, { code: 'VI', name: 'U.S. Virgin Islands' }, { code: 'GU', name: 'Guam' },
  { code: 'AS', name: 'American Samoa' }, { code: 'MP', name: 'Northern Mariana Islands' },
];

/** 10 provinces + 3 territories. */
export const CA_PROVINCES: AddressRegion[] = [
  { code: 'AB', name: 'Alberta' }, { code: 'BC', name: 'British Columbia' }, { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' }, { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' }, { code: 'NT', name: 'Northwest Territories' }, { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' }, { code: 'PE', name: 'Prince Edward Island' }, { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' }, { code: 'YT', name: 'Yukon' },
];

/** Normalizes a free-form country value to ISO alpha-2 ('US' | 'CA'). Unknown → 'US'. */
export function normalizeCountryToIso(country?: string | null): 'US' | 'CA' {
  const c = (country ?? '').trim().toUpperCase();
  if (c === 'CA' || c === 'CAN' || c === 'CANADA') return 'CA';
  return 'US';
}

/** True when the country is one we have region data for (US or Canada). */
export function isUsOrCanada(country?: string | null): boolean {
  const c = (country ?? '').trim().toUpperCase();
  return c === 'US' || c === 'USA' || c === 'UNITED STATES'
    || c === 'CA' || c === 'CAN' || c === 'CANADA';
}

/** Regions (states or provinces) for the given country. Defaults to US. */
export function getRegionsForCountry(country?: string | null): AddressRegion[] {
  return normalizeCountryToIso(country) === 'CA' ? CA_PROVINCES : US_STATES;
}

/** True if `code` is a valid 2-letter region code for the country. */
export function isValidRegionCode(code: string, country?: string | null): boolean {
  const upper = (code ?? '').trim().toUpperCase();
  return getRegionsForCountry(country).some((r) => r.code === upper);
}

/**
 * Resolves a state/province name or code to its 2-letter code for the country.
 * Returns the input unchanged when it can't be resolved (matches prior passthrough behavior).
 */
export function getRegionCode(nameOrCode: string, country?: string | null): string {
  const input = (nameOrCode ?? '').trim();
  if (!input) return nameOrCode;
  const upper = input.toUpperCase();
  const regions = getRegionsForCountry(country);
  const byCode = regions.find((r) => r.code === upper);
  if (byCode) return byCode.code;
  const byName = regions.find((r) => r.name.toUpperCase() === upper);
  return byName ? byName.code : nameOrCode;
}

/** UI label for the region field, country-aware. */
export function getRegionLabel(country?: string | null): 'Province' | 'State' {
  return normalizeCountryToIso(country) === 'CA' ? 'Province' : 'State';
}

/** UI label for the postal/ZIP code field, country-aware. */
export function getPostalCodeLabel(country?: string | null): 'Postal Code' | 'ZIP Code' {
  return normalizeCountryToIso(country) === 'CA' ? 'Postal Code' : 'ZIP Code';
}

/** Placeholder for the postal/ZIP code input, country-aware. */
export function getPostalCodePlaceholder(country?: string | null): string {
  return normalizeCountryToIso(country) === 'CA' ? 'A1A 1A1' : '12345';
}

/**
 * True if `value` is a valid postal/ZIP code for the country.
 * US: 5-digit ZIP with optional +4. CA: A1A 1A1 (separator optional).
 */
export function isValidPostalCode(value: string, country?: string | null): boolean {
  const v = (value ?? '').trim();
  if (normalizeCountryToIso(country) === 'CA') {
    return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(v);
  }
  return /^\d{5}(-\d{4})?$/.test(v);
}

/** Country-aware validation message for an invalid postal/ZIP code. */
export function getPostalCodeError(country?: string | null): string {
  return normalizeCountryToIso(country) === 'CA'
    ? 'Please enter a valid postal code (e.g. A1A 1A1)'
    : 'Please enter a valid 5-digit ZIP code';
}

/**
 * Reduce a postal/ZIP code to its service-area KEY for storage and matching.
 * US: first 5 digits (handles ZIP+4). CA: FSA = first 3 alphanumeric chars (e.g. "V6S").
 * Returns '' when no key can be formed.
 *
 * MUST mirror the backend PostalCodeUtil.NormalizeForServiceArea exactly — the
 * GetSlots / SmartAssignment territory match normalizes both the stored code and
 * the customer's code with the equivalent of this function, so the keys must agree.
 */
export function normalizeServiceAreaCode(code: string, country?: string | null): string {
  const raw = (code ?? '').trim().toUpperCase();
  if (!raw) return '';
  if (normalizeCountryToIso(country) === 'CA') {
    return raw.replace(/[^A-Z0-9]/g, '').slice(0, 3);
  }
  return raw.replace(/[^0-9]/g, '').slice(0, 5);
}

/**
 * True if `code` is a usable service-area entry for the country — i.e. it
 * normalizes to a complete key (CA: 3-char FSA; US: 5-digit ZIP).
 */
export function isValidServiceAreaCode(code: string, country?: string | null): boolean {
  const key = normalizeServiceAreaCode(code, country);
  if (normalizeCountryToIso(country) === 'CA') {
    // FSA: letter, digit, letter
    return /^[A-Z]\d[A-Z]$/.test(key);
  }
  return /^\d{5}$/.test(key);
}
